import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Types
export interface PaymentMethod {
  type: 'creditCard' | 'bankAccount';
  cardType?: string;
  lastFour?: string;
  expiryMonth?: string;
  expiryYear?: string;
  accountType?: string;
  routingNumber?: string;
  accountNumber?: string;
}

export interface PaymentFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  billingAddress: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  paymentMethod: PaymentMethod;
  termsAccepted: boolean;
  marketingConsent: boolean;
}

export interface PaymentState {
  formData: PaymentFormData;
  loading: boolean;
  error: string | null;
  success: boolean;
  orderId: string | null;
}

export interface PaymentContextType extends PaymentState {
  updateFormData: (data: Partial<PaymentFormData>) => void;
  validateForm: () => boolean;
  processPayment: () => Promise<boolean>;
  resetPayment: () => void;
  setPaymentMethod: (method: PaymentMethod) => void;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

interface PaymentProviderProps {
  children: ReactNode;
}

/**
 * Payment Context - Templatized Config-Based Approach
 * 
 * Manages payment state following the same patterns as the .NET cart:
 * - Credit card and bank account processing
 * - Partner-specific payment options
 * - Form validation and submission
 * - Order creation and tracking
 */

export const PaymentProvider: React.FC<PaymentProviderProps> = ({ children }) => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const [state, setState] = useState<PaymentState>({
    formData: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      billingAddress: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US'
      },
      paymentMethod: {
        type: 'creditCard'
      },
      termsAccepted: false,
      marketingConsent: false
    },
    loading: false,
    error: null,
    success: false,
    orderId: null
  });

  // Load pre-filled data for partner leads
  React.useEffect(() => {
    const loadPartnerLeadData = () => {
      const storedData = sessionStorage.getItem('partnerLeadData');
      if (storedData) {
        try {
          const partnerData = JSON.parse(storedData);
          setState(prev => ({
            ...prev,
            formData: {
              ...prev.formData,
              firstName: partnerData.firstName || '',
              lastName: partnerData.lastName || '',
              email: partnerData.email || '',
              phone: partnerData.phone || ''
            }
          }));
        } catch (error) {
          console.error('Error parsing partner lead data:', error);
        }
      }
    };

    loadPartnerLeadData();
  }, []);

  const updateFormData = (data: Partial<PaymentFormData>) => {
    setState(prev => ({
      ...prev,
      formData: { ...prev.formData, ...data }
    }));
  };

  const setPaymentMethod = (method: PaymentMethod) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        paymentMethod: method
      }
    }));
  };

  const validateForm = (): boolean => {
    const { formData } = state;
    
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setState(prev => ({ ...prev, error: 'Please fill in all required fields' }));
      return false;
    }

    if (!formData.termsAccepted) {
      setState(prev => ({ ...prev, error: 'Please accept the terms and conditions' }));
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setState(prev => ({ ...prev, error: 'Please enter a valid email address' }));
      return false;
    }

    // Payment method validation
    if (formData.paymentMethod.type === 'creditCard') {
      if (!formData.paymentMethod.cardType || !formData.paymentMethod.lastFour) {
        setState(prev => ({ ...prev, error: 'Please enter valid credit card information' }));
        return false;
      }
    } else if (formData.paymentMethod.type === 'bankAccount') {
      if (!formData.paymentMethod.routingNumber || !formData.paymentMethod.accountNumber) {
        setState(prev => ({ ...prev, error: 'Please enter valid bank account information' }));
        return false;
      }
    }

    setState(prev => ({ ...prev, error: null }));
    return true;
  };

  const processPayment = async (): Promise<boolean> => {
    if (!validateForm()) {
      return false;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Use partnerId from URL params instead of extracting from path
      const selectedProductId = sessionStorage.getItem('selectedProductId');

      const paymentData = {
        partnerId,
        productId: selectedProductId,
        ...state.formData
      };

      const response = await axios.post(`/api/partners/${partnerId}/signup`, paymentData);

      if (response.data.success) {
        setState(prev => ({
          ...prev,
          loading: false,
          success: true,
          orderId: response.data.orderId
        }));

        // Store order data for success page
        sessionStorage.setItem('orderData', JSON.stringify(response.data));
        return true;
      } else {
        throw new Error(response.data.error || 'Payment processing failed');
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Payment processing failed'
      }));
      return false;
    }
  };

  const resetPayment = () => {
    setState({
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        billingAddress: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US'
        },
        paymentMethod: {
          type: 'creditCard'
        },
        termsAccepted: false,
        marketingConsent: false
      },
      loading: false,
      error: null,
      success: false,
      orderId: null
    });
  };

  const value: PaymentContextType = {
    ...state,
    updateFormData,
    validateForm,
    processPayment,
    resetPayment,
    setPaymentMethod
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = (): PaymentContextType => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
}; 